import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { Plus, Search, X, Pencil } from "lucide-react";
import Pagination from "@/components/Pagination";

export default function Returns() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = () => api.get("/returns").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((ret) => {
      if (q) {
        const styleIds = (ret.items || []).map((it) => it.style_id || "").join(" ");
        const hay = `${ret.customer_snapshot?.name || ""} ${ret.return_number || ""} ${styleIds}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (from && (ret.return_date || "") < from) return false;
      if (to && (ret.return_date || "") > to) return false;
      return true;
    });
  }, [items, query, from, to]);

  const totals = useMemo(() => {
    let refunded = 0;
    filtered.forEach((r) => { refunded += Number(r.amount_returned || 0); });
    return { refunded };
  }, [filtered]);

  const clearFilters = () => { setQuery(""); setFrom(""); setTo(""); };
  const hasFilters = query || from || to;

  useEffect(() => { setPage(1); }, [query, from, to, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <div data-testid="returns-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Returns</h1>
          <p className="page-sub">{filtered.length} of {items.length} return(s)</p>
        </div>
        <Link to="/returns/new" className="btn btn-primary" data-testid="new-return-btn"><Plus size={16}/> New Return</Link>
      </div>

      <div className="card" style={{ marginBottom: 16 }} data-testid="return-filters">
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div className="field">
            <label>Search customer / return # / style</label>
            <div className="search-wrap">
              <Search size={16}/>
              <input data-testid="return-search" className="input" placeholder="e.g. Acme Buyer, RET-2026-..., or BK-01" value={query} onChange={(e) => setQuery(e.target.value)}/>
            </div>
          </div>
          <div className="field">
            <label>From</label>
            <input data-testid="return-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)}/>
          </div>
          <div className="field">
            <label>To</label>
            <input data-testid="return-to" className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)}/>
          </div>
          <button className="btn btn-ghost btn-sm" data-testid="clear-return-filters" onClick={clearFilters} disabled={!hasFilters} style={{ height: 36 }}><X size={14}/> Clear</button>
        </div>
        {hasFilters && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#F8FAFC", border: "1px solid var(--border)", borderRadius: 4, fontSize: 13 }} data-testid="return-filter-totals">
            <span>Total Refunded: <strong className="tabular">₹ {fmt(totals.refunded)}</strong></span>
          </div>
        )}
      </div>

      <div className="card-flush">
        <table className="tbl">
          <thead><tr><th>Return #</th><th>Date</th><th>Customer</th><th>Items</th><th className="num">Amount Returned</th><th>Refund Method</th><th>Note</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>{items.length === 0 ? "No returns yet." : "No returns match the filters."}</td></tr>}
            {paged.map((ret) => (
              <tr key={ret.id} data-testid={`return-row-${ret.return_number}`}>
                <td><Link to={`/returns/${ret.id}`} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>{ret.return_number}</Link></td>
                <td>{ret.return_date}</td>
                <td>{ret.customer_snapshot?.name}</td>
                <td>{ret.items?.length}</td>
                <td className="num"><strong>₹ {fmt(ret.amount_returned || 0)}</strong></td>
                <td>{ret.refund_method ? ret.refund_method.toUpperCase() : "—"}</td>
                <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{ret.note || "—"}</td>
                <td>
                  <Link to={`/returns/${ret.id}/edit`} className="btn btn-ghost btn-sm" data-testid={`edit-return-${ret.return_number}`} title="Edit return"><Pencil size={14}/></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={setPage} onPageSizeChange={setPageSize}/>
      </div>
    </div>
  );
}
